/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useState } from 'react'
import { usePathname } from 'lib/i18n/routing'
import { FlightIcon, Text } from '@hashicorp/mds-react/components'
import { m } from 'motion/react'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import s from './style.module.css'

interface SocialShareProps {
	summary: string
}

export default function SocialShare({ summary }: SocialShareProps) {
	const path = usePathname()

	const [isTooltipActive, setIsTooltipActive] = useState(false)
	const [isTooltipTextPresent, setIsTooltipTextPresent] = useState(false)
	const url = path
		? `https://www.hashicorp.com${path}`
		: 'https://www.hashicorp.com'

	const tooltipVariants = {
		active: { opacity: 1, y: -10, scale: 1 },
		inactive: { opacity: 0, y: 0, scale: 0.85 },
	}

	const tooltipCheckVariants = {
		inactive: {
			pathOffset: 1,
			pathLength: 1,
		},
		active: {
			pathOffset: 0,
			pathLength: 1,
		},
	}

	async function copyUrl() {
		if (isTooltipActive || isTooltipTextPresent) {
			return
		}

		try {
			await navigator.clipboard.writeText(url)
			setIsTooltipActive(true)
			setIsTooltipTextPresent(true)
			setTimeout(() => {
				setIsTooltipActive(false)
			}, 2000)
			setTimeout(() => {
				setIsTooltipTextPresent(false)
			}, 2250)
		} catch (err) {
			console.log(err)
		}
	}

	function openShare(url) {
		window.open(url, '_blank', 'popup, width=550, height=700')
	}

	return (
		<ul className={s.socialShare}>
			<li>
				<button
					className={s.shareButton}
					onClick={() => {
						openShare(
							`https://twitter.com/intent/tweet?text=${
								summary || ''
							}&url=${encodeURIComponent(url)}`,
						)
					}}
				>
					<VisuallyHidden>Twitter share</VisuallyHidden>
					<FlightIcon className={s.icon} name="twitter" size={24} />
				</button>
			</li>
			<li>
				<button
					className={s.shareButton}
					onClick={() => {
						openShare(
							`https://www.linkedin.com/sharing/share-offsite/?text=${
								summary || ''
							}&url=${encodeURIComponent(url)}`,
						)
					}}
				>
					<VisuallyHidden>LinkedIn share</VisuallyHidden>
					<FlightIcon className={s.icon} name="linkedin" size={24} />
				</button>
			</li>
			<li>
				<button
					className={s.shareButton}
					onClick={() => {
						openShare(
							`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
								url,
							)}`,
						)
					}}
				>
					<VisuallyHidden>Facebook share</VisuallyHidden>
					<FlightIcon className={s.icon} name="facebook" size={24} />
				</button>
			</li>
			<li>
				<button
					className={s.shareButton}
					onClick={async () => {
						await copyUrl()
					}}
					aria-controls="copy-url-tooltip"
				>
					<VisuallyHidden>Copy URL</VisuallyHidden>
					<FlightIcon className={s.icon} name="link" size={24} />
				</button>
				<m.div
					id="copy-url-tooltip"
					initial={{ opacity: 0 }}
					animate={isTooltipActive ? 'active' : 'inactive'}
					variants={tooltipVariants}
					transition={{
						duration: 0.25,
					}}
					className={s.tooltip}
					aria-live="polite"
					role="status"
				>
					{isTooltipTextPresent ? (
						<Text.Body size="200" color="strong">
							Copied{' '}
						</Text.Body>
					) : null}
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<m.path
							d="M20 6 9 17l-5-5"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							animate={isTooltipActive ? 'active' : 'inactive'}
							variants={tooltipCheckVariants}
							transition={{
								delay: 0.15,
							}}
						/>
					</svg>
				</m.div>
			</li>
		</ul>
	)
}
